import { create } from "@/store";
import { subscribeWithSelector } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { deleteCameraHistoryEvents } from "viewer/modules/cloud/media";
import { useActivityLog } from "viewer/store/activityLog";
import ActivityLogController from "viewer/modules/activityLog/ActivityLogController";

const initialState: EventDeletionState = {
  objectIds: [],
  lastAddedObjectId: null,
  isInDeletionMode: false,
  isProcessing: false,
  error: null
};

const useEventDeletion = create<EventDeletionStore>()(
  subscribeWithSelector(
    immer((set, get) => ({
      ...initialState,
      add(objectId) {
        set((state) => {
          if (state.objectIds.includes(objectId)) return;
          state.objectIds.push(objectId);
          state.lastAddedObjectId = objectId;
        });
      },
      remove(objectId) {
        set((state) => {
          state.objectIds = state.objectIds.filter((i) => i !== objectId);
          if (objectId === get().lastAddedObjectId) {
            const ids = state.objectIds;
            const newLastAddedObjectId = ids.length ? ids[ids.length - 1] : null;
            state.lastAddedObjectId = newLastAddedObjectId;
          }
        });
      },
      clearSelection() {
        set((state) => {
          state.objectIds = [];
          state.lastAddedObjectId = null;
        });
      },
      addOrRemove(objectId) {
        set((state) => {
          if (state.objectIds.includes(objectId)) state.objectIds = state.objectIds.filter((i) => i !== objectId);
          else state.objectIds.push(objectId);
          state.lastAddedObjectId = objectId;
        });
      },
      addMultiple(objectId: string) {
        const events = useActivityLog.getState().events;
        const lastId = get().lastAddedObjectId || objectId;
        const startIndex = events.findIndex((event) => event.objectId === lastId);
        const endIndex = events.findIndex((event) => event.objectId === objectId);

        const rangeStart = Math.min(startIndex, endIndex);
        const rangeEnd = Math.max(startIndex, endIndex);
        const eventsToAdd = events.slice(rangeStart, rangeEnd + 1);

        set((state) => {
          state.objectIds = [...new Set([...state.objectIds, ...eventsToAdd.map((event) => event.objectId)])];
          state.lastAddedObjectId = objectId;
        });
      },
      addAll() {
        const eventIds = useActivityLog
          .getState()
          .events.filter((event) => event.end && event.intervalEvent)
          .map((event) => event.objectId);
        set((state) => {
          state.objectIds = [...new Set([...state.objectIds, ...eventIds])];
          state.lastAddedObjectId = eventIds[eventIds.length - 1];
        });
      },
      toggleDeletionMode() {
        const isInDeletionMode = !get().isInDeletionMode;
        if (isInDeletionMode) set({ isInDeletionMode: true });
        else set(initialState);
      },
      setDeletionMode(isInDeletionMode) {
        set({ isInDeletionMode });
      },
      async deleteSelectedEvents() {
        const ids = get().objectIds;
        if (!ids.length) {
          log.info("No events to delete");
          return;
        }
        log.info("Deleting events... ", ids);
        try {
          set({ isProcessing: true, error: null });
          const response = await deleteCameraHistoryEvents(ids);
          log.info("Deleted events ", response);
          set({ objectIds: [] });

          const { events, setEvents, selectedEventId, setSelectedEventId } = useActivityLog.getState();
          const newEvents = events.filter((event) => !ids.includes(event.objectId));
          if (newEvents.length) {
            setEvents(newEvents);
            if (selectedEventId) {
              const didRemoveSelectedEvent = !newEvents.find((event) => event.uniqueId === selectedEventId);
              if (didRemoveSelectedEvent) setSelectedEventId(newEvents[0]?.uniqueId);
            }
            const deletedEventsCount = events.filter((event) => ids.includes(event.objectId)).length;
            ActivityLogController.downloadNextEvents({ count: deletedEventsCount });
          } else {
            ActivityLogController.downloadEvents();
          }
        } catch (err: any) {
          log.err("Failed to delete events", err);
          set({ error: err?.message || "Unknown error" });
        } finally {
          set({ isProcessing: false });
        }
      },
      resetState() {
        set(initialState);
      }
    }))
  )
);

const eventDeletionStore = useEventDeletion.getState;
export { useEventDeletion, eventDeletionStore };
